/**
 * AddProposalRequestModal — create or edit a Proposal Request (architect-issued), scoped to a contract.
 *
 * Opens via CustomEvent('open-add-proposal-request-modal', { detail }):
 *   create: { contractId, projectId }
 *   edit:   { contractId, projectId, prId, prefill: {...} }
 *
 * Busts ctx.proposalRequestCache[contractId] + dispatches outline-render on success.
 */

import { useState, useEffect } from 'react'
import { ctx } from '../outline/ctx.js'
import { createProposalRequest, updateProposalRequest } from '../services/proposalRequests.js'
import Select from './Select.jsx'
import MarkdownField from './MarkdownField.jsx'

const STATUS_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'proposal_received', label: 'Proposal received' },
  { value: 'closed', label: 'Closed' },
  { value: 'void', label: 'Void' },
]

const labelStyle = { display: 'block', fontFamily: '"DM Mono",monospace', fontSize: 10, color: 'var(--aj-text-mid)', letterSpacing: '0.04em', marginBottom: 4, textTransform: 'uppercase' }
const inputStyle = { width: '100%', padding: '7px 10px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, boxSizing: 'border-box', outline: 'none' }

// attachment_list (jsonb [{filename,url}]) ⇄ a "label | url" per-line textarea.
function attListToText(list) {
  if (!Array.isArray(list)) return ''
  return list.map(a => (a.filename ? a.filename + ' | ' : '') + (a.url || '')).join('\n')
}
function textToAttList(text) {
  return String(text || '').split('\n').map(l => l.trim()).filter(Boolean).map(line => {
    const i = line.indexOf(' | ')
    if (i >= 0) return { filename: line.slice(0, i).trim(), url: line.slice(i + 3).trim() }
    return { filename: line.split('/').pop() || 'Attachment', url: line }
  }).filter(a => a.url)
}

export default function AddProposalRequestModal() {
  const [open, setOpen]             = useState(false)
  const [contractId, setContractId] = useState(null)
  const [projectId, setProjectId]   = useState(null)
  const [editId, setEditId]         = useState(null)
  const [prNumber, setPrNumber]     = useState('')
  const [subject, setSubject]       = useState('')
  const [issueDate, setIssueDate]   = useState('')
  const [dueDate, setDueDate]       = useState('')
  const [status, setStatus]         = useState('open')
  const [description, setDescription] = useState('')
  const [attachText, setAttachText] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError]           = useState(null)

  useEffect(() => {
    if (open) { document.body.style.overflow = 'hidden'; return () => { document.body.style.overflow = '' } }
  }, [open])

  useEffect(() => {
    function onOpen(e) {
      const d = e.detail || {}
      setContractId(d.contractId || null)
      setProjectId(d.projectId || null)
      setEditId(d.prId || null)
      const p = d.prefill || {}
      setPrNumber(p.pr_number || '')
      setSubject(p.subject || '')
      setIssueDate(p.issue_date || (d.prId ? '' : new Date().toISOString().slice(0, 10)))
      setDueDate(p.due_date || '')
      setStatus(p.status || 'open')
      setDescription(p.description || '')
      setAttachText(attListToText(p.attachment_list))
      setError(null); setSubmitting(false); setOpen(true)
    }
    window.addEventListener('open-add-proposal-request-modal', onOpen)
    return () => window.removeEventListener('open-add-proposal-request-modal', onOpen)
  }, [])

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape' && open) setOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  async function handleSubmit() {
    setSubmitting(true); setError(null)
    const fields = {
      pr_number: prNumber || null,
      subject: subject || null,
      issue_date: issueDate || null,
      due_date: dueDate || null,
      status,
      description: description || null,
      attachment_list: textToAttList(attachText),
    }
    try {
      if (editId) await updateProposalRequest(editId, fields)
      else {
        if (!contractId) { setError('No contract in context.'); setSubmitting(false); return }
        await createProposalRequest(contractId, projectId, fields)
      }
      if (ctx.proposalRequestCache && contractId) ctx.proposalRequestCache[contractId] = undefined
      setOpen(false)
      window.dispatchEvent(new Event('outline-render'))
    } catch (ex) {
      setError(ex.message || 'Failed to save proposal request'); setSubmitting(false)
    }
  }

  if (!open) return null

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div data-modal-id="AddProposalRequestModal" style={{ background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 8, boxShadow: '0 12px 36px rgba(0,0,0,0.16)', width: 480, padding: '20px 20px 16px', maxHeight: 'calc(100vh - 40px)', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: 'var(--aj-text)' }}>{editId ? 'Edit Proposal Request' : 'Add Proposal Request'}</span>
          <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-mid)', fontSize: 16, lineHeight: 1, padding: 2 }}>×</button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>PR #</label>
              <input style={inputStyle} value={prNumber} onChange={e => setPrNumber(e.target.value)} placeholder="PR-01" />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Issue Date</label>
              <input type="date" style={inputStyle} value={issueDate} onChange={e => setIssueDate(e.target.value)} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Response Due</label>
              <input type="date" style={inputStyle} value={dueDate} onChange={e => setDueDate(e.target.value)} />
            </div>
          </div>
          {editId && (
            <div style={{ width: 200 }}>
              <label style={labelStyle}>Status</label>
              <Select value={status} onChange={setStatus} options={STATUS_OPTIONS} style={{ width: '100%', fontSize: 12 }} />
            </div>
          )}
          <div>
            <label style={labelStyle}>Subject</label>
            <input style={inputStyle} value={subject} onChange={e => setSubject(e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Description</label>
            <MarkdownField value={description} onChange={setDescription} />
          </div>
          <div>
            <label style={labelStyle}>Attachments (one per line — “label | https://…”)</label>
            <textarea style={{ ...inputStyle, minHeight: 60, resize: 'vertical', fontFamily: '"DM Mono",monospace', fontSize: 11 }}
              value={attachText} onChange={e => setAttachText(e.target.value)} placeholder="Sketch SK-1 | https://…" />
          </div>

          {error && <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: '#c0392b' }}>{error}</div>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
            <button onClick={() => setOpen(false)} style={{ padding: '6px 14px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, cursor: 'pointer', color: 'var(--aj-text)' }}>Cancel</button>
            <button onClick={handleSubmit} disabled={submitting} style={{ padding: '6px 16px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, background: submitting ? 'var(--aj-bg-muted)' : '#07272D', color: submitting ? 'var(--aj-text-muted)' : '#fff', border: 'none', borderRadius: 4, cursor: submitting ? 'default' : 'pointer' }}>
              {submitting ? 'Saving…' : (editId ? 'Save' : 'Issue Proposal Request')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
